import { useRef } from 'react'
import type ReactECharts from 'echarts-for-react'
import { useRiskFilters } from '../../../../shared/hooks/useRiskFilters'
import { RiskProfileCard } from '../../RiskProfileCard'
import { ChartQueryState } from '../../ChartQueryState'
import { ChartExportActions } from '../../../chart/ChartExportActions'
import { useExportXlsx } from '../../../hooks/useExportXlsx'
import { useExportChartImage } from '../../../hooks/useExportChartImage'
import { useExportDomImage } from '../../../hooks/useExportDomImage'
import RiskByElementsChart from '../../../chart/RiskByElements.chart'
import { RiskByElementSummary } from '../../RiskByElementSummary'
import { useProfileElementTypeData } from '../../../hooks/useProfileElementTypeData'
import type { RiskProfileQueryParams } from '../../../hooks/useRiskProfileQueryParams'
import type { RiskProfileMetricOption } from '../../../types/riskProfile.types'

export interface RiskByElementWidgetProps {
  queryParams: RiskProfileQueryParams | null
  metricOption: RiskProfileMetricOption
}

export function RiskByElementWidget({ queryParams, metricOption }: RiskByElementWidgetProps) {
  const { setFilters } = useRiskFilters()
  const { request, query } = useProfileElementTypeData(queryParams)
  const { exportXlsx, isExporting } = useExportXlsx(request)
  const chartRef = useRef<ReactECharts>(null)
  const summaryRef = useRef<HTMLDivElement>(null)
  const handleExportChartImage = useExportChartImage(chartRef, 'risk-by-element-type')
  const handleExportSummaryImage = useExportDomImage(summaryRef, 'risk-by-element-type-summary')

  const handleElementClick = (clickedTitle: string) => {
    const entry = Object.entries(queryParams?.elementTitles ?? {}).find(
      ([, title]) => title === clickedTitle,
    )
    const elementId = entry ? entry[0] : clickedTitle
    setFilters({ elementGroups: [elementId] })
  }

  const isSingleElement = (data: { groups: unknown[] }) => data.groups.length === 1
  const showingSummary = isSingleElement(query.data ?? { groups: [] })

  return (
    <RiskProfileCard
      title={
        showingSummary ? `Risk to ${query.data?.groups[0]?.label ?? ''}` : 'Risk by element type'
      }
      description="Ranked exposure by element for the selected hazards and metric."
      onExport={
        showingSummary ? (
          <ChartExportActions
            onExportXlsx={exportXlsx}
            onExportImage={handleExportSummaryImage}
            isExportingXlsx={isExporting}
            xlsxDisabled={!request}
          />
        ) : (
          <ChartExportActions
            onExportXlsx={exportXlsx}
            onExportImage={handleExportChartImage}
            isExportingXlsx={isExporting}
            xlsxDisabled={!request}
          />
        )
      }
      className="min-h-96"
    >
      <ChartQueryState query={query}>
        {(data) =>
          isSingleElement(data) ? (
            <RiskByElementSummary ref={summaryRef} data={data} metricOption={metricOption} />
          ) : (
            <RiskByElementsChart
              ref={chartRef}
              data={data}
              metricOption={metricOption}
              onItemClick={handleElementClick}
            />
          )
        }
      </ChartQueryState>
    </RiskProfileCard>
  )
}
